
import { Component, Prop, Vue } from 'vue-property-decorator'
import Icon from '@/components/Icon.vue'
import { Company } from '@/models/Company'
import { Country } from '@/models/Country'
import { PhoneCountryCode } from '@/models/PhoneCountryCode'
import { Vessel } from '@/models/Vessel'
import { namespace } from 'vuex-class'
import User from '@/models/User'

const Auth = namespace('auth')

@Component({
	components: {
		Icon
	}
})
export default class CompanyEditor extends Vue {
	@Prop({ default: () => new Company() })
	company!: Company

	@Prop({ default: true })
	activeCompany!: boolean

	@Prop({ default: () => new Vessel() })
	vessel!: Vessel

	@Prop({ default: null, required: false })
	relationType: string | null | undefined

	@Auth.Getter('isSkipper')
	isSkipper

	@Auth.Getter('isBroker')
	isBroker

	companyName: string = ''
	reference: string | null = null
	email: string = ''
	vatNumber: string = ''
	cocNumber: number | null = null
	termOfPaymentDay: number | null = null
	iban: string = ''
	streetName: string = ''
	postalCode: string = ''
	place: string = ''
	country: Country | null = null
	website: string = ''
	note: string | null = null
	phoneCountryCode: PhoneCountryCode | null = null
	phoneNumber: string = ''
	relationTypes: string | null | undefined = null

	hasSecondaryPhoneNumber: boolean = false
	secondaryPhoneCountryCode: PhoneCountryCode | null = null
	secondaryPhoneNumber: string = ''

	hasTertiaryPhoneNumber: boolean = false
	tertiaryPhoneCountryCode: PhoneCountryCode | null = null
	tertiaryPhoneNumber: string = ''

	hasAlternativeInvoiceDetails: boolean = false
	invoiceEmail: string = ''
	invoiceStreetName: string = ''
	invoicePostalCode: string = ''
	invoicePlace: string = ''
	invoiceCountry: Country | null = null

	hasAlternativeMailDetails: boolean = false
	mailStreetName: string = ''
	mailPostalCode: string = ''
	mailPlace: string = ''
	mailCountry: Country | null = null

	phoneCountryCodes: Array<PhoneCountryCode> = []
	countries: Array<Country> = []
	isLoading = true

	user: User | null = null

	get invoiceEmailRequired() {
		return this.hasAlternativeInvoiceDetails && !this.invoiceStreetName
	}

	get invoiceAddressRequired() {
		return this.hasAlternativeInvoiceDetails && !this.invoiceEmail
	}

	async created() {
		const countriesQuery = Country.fetchAllLazy()
		const phoneCountryCodesQuery = PhoneCountryCode.fetchAllLazy()
		const userQuery = User.fetchMeLazy()
		if (this.company) {
			this.companyName = this.company.name as string
			this.reference = this.company.reference as string
			this.note = this.company.notes as string
			this.email = this.company.email as string
			this.vatNumber = this.company.vatNumber as string
			this.cocNumber = this.company.cocNumber as number
			this.termOfPaymentDay = this.company.paymentTermDays as number
			this.iban = this.company.iban as string
			this.streetName = this.company.address as string
			this.postalCode = this.company.postalCode as string
			this.place = this.company.place as string
			this.country = this.company.getCountry()
			this.website = this.company.website as string
			this.relationTypes = this.company.relationTypes

			this.phoneCountryCode = this.company.getPrimaryPhoneCountryCode()
			this.phoneNumber = this.company.phoneNumber as string

			this.hasSecondaryPhoneNumber = !!this.company.alternativePhoneNumber
			this.secondaryPhoneCountryCode = this.company.getSecondaryPhoneCountryCode()
			this.secondaryPhoneNumber = this.company.alternativePhoneNumber as string

			this.hasTertiaryPhoneNumber = !!this.company.secondAlternativePhoneNumber
			this.tertiaryPhoneCountryCode = this.company.getTertiaryPhoneCountryCode()
			this.tertiaryPhoneNumber = this.company.secondAlternativePhoneNumber as string

			this.hasAlternativeInvoiceDetails = this.company.hasAlternativeInvoiceDetails as boolean
			this.invoiceEmail = this.company.alternativeInvoiceEmail as string
			this.invoiceStreetName = this.company.alternativeInvoiceAddress as string
			this.invoicePostalCode = this.company.alternativeInvoicePostalCode as string
			this.invoicePlace = this.company.alternativeInvoicePlace as string
			this.invoiceCountry = this.company.getInvoiceCountry()

			this.hasAlternativeMailDetails = this.company.hasAlternativeMailDetails as boolean
			this.mailStreetName = this.company.alternativeMailAddress as string
			this.mailPostalCode = this.company.alternativeMailPostalCode as string
			this.mailPlace = this.company.alternativeMailPlace as string
			this.mailCountry = this.company.getMailCountry()
		}

		this.phoneCountryCodes = await phoneCountryCodesQuery
		this.countries = await countriesQuery
		this.user = await userQuery

		this.isLoading = false
	}

	async validate() {
		return await this.$validator.validateAll()
	}

	get dynamicRelationType() {
		if (this.$route.path === '/groups/clients') return 'client'
		if (this.$route.path === '/groups/brokers' || this.isBroker) return 'broker'
		if (this.$route.path === '/groups/vessels') {
			return !this.vessel.isRegistered ? 'unregistered_vessel' : 'registered_vessel'
		}

		if (this.isSkipper) {
			return !this.user?.getVessels()[0].isRegistered ? 'unregistered_vessel' : 'registered_vessel'
		}

		return null
	}

	async submit(returnReceivedModel = false) {
		const company = this.company || new Company()

		company.relationTypes = this.relationTypes ?? this.relationType ?? this.dynamicRelationType

		company.name = this.companyName

		company.reference = this.reference?.length ? this.reference : null

		company.email = this.email
		company.vatNumber = this.vatNumber
		company.cocNumber = this.cocNumber
		company.iban = this.iban
		company.address = this.streetName
		company.postalCode = this.postalCode
		company.place = this.place
		company.website = this.website
		company.paymentTermDays = this.termOfPaymentDay
		company.notes = this.note
		company.phoneNumber = this.phoneNumber

		company.alternativePhoneNumber = this.secondaryPhoneNumber
		company.secondAlternativePhoneNumber = this.tertiaryPhoneNumber

		company.hasAlternativeInvoiceDetails = this.hasAlternativeInvoiceDetails
		if (this.hasAlternativeInvoiceDetails) {
			company.alternativeInvoiceEmail = this.invoiceEmail
			company.alternativeInvoiceAddress = this.invoiceStreetName
			company.alternativeInvoicePostalCode = this.invoicePostalCode
			company.alternativeInvoicePlace = this.invoicePlace
		}

		company.hasAlternativeMailDetails = this.hasAlternativeMailDetails
		if (this.hasAlternativeMailDetails) {
			company.alternativeMailAddress = this.mailStreetName
			company.alternativeMailPostalCode = this.mailPostalCode
			company.alternativeMailPlace = this.mailPlace
		}

		if (!this.hasSecondaryPhoneNumber) {
			company.alternativePhoneNumber = null
		}

		if (!this.hasTertiaryPhoneNumber) {
			company.secondAlternativePhoneNumber = null
		}

		company.setCountry(this.country)
		company.setInvoiceCountry(this.invoiceCountry)
		company.setMailCountry(this.mailCountry)
		company.setPrimaryPhoneCountryCode(this.phoneCountryCode)
		company.setSecondaryPhoneCountryCode(this.secondaryPhoneCountryCode)
		company.setTertiaryPhoneCountryCode(this.tertiaryPhoneCountryCode)

		const received = await company.save()
		if (this.activeCompany) {
			// refresh the state of the profile completeness
			await User.sync(true)
		}
		if (returnReceivedModel) {
			return received
		}

		return company
	}
}
